import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { amplifyApi } from '../services/amplifyApi';
import PricingTab from './admin/PricingTab';
import UsersTab from './admin/UsersTab';
import CommissionsTab from './admin/CommissionsTab';
import ProductsTab from './admin/ProductsTab';
import AiStatsTab from './admin/AiStatsTab';
import LimitsTab from './admin/LimitsTab';
import MonetizationTab from './admin/MonetizationTab';
import { useBrand } from '../context/BrandContext';
import {
  LayoutDashboard,
  DollarSign,
  Users,
  Receipt,
  Package,
  Sparkles,
  ArrowLeft,
  ToggleLeft,
  ToggleRight,
  Loader2,
  AlertCircle,
  Gauge,
  Lock,
  Megaphone
} from 'lucide-react';

function AdminDashboard() {
  const { user, signOut } = useAuth();
  const brand = useBrand();

  // Check if this brand uses fixed pricing mode (no matrix toggle allowed)
  const isFixedPricingMode = brand?.features?.pricing?.mode === 'fixed';
  // Check if referrals/commissions are enabled for this brand
  const isReferralsEnabled = brand?.features?.referrals?.enabled !== false;
  // Check if AI generation is enabled for this brand
  const isAiEnabled = brand?.features?.aiGeneration?.enabled !== false;
  // Check if monetization is enabled for this brand
  const isMonetizationEnabled = brand?.features?.monetization?.enabled !== false;
  const [activeTab, setActiveTab] = useState('pricing');
  const [pricingMatrixEnabled, setPricingMatrixEnabled] = useState(false);
  const [loadingToggle, setLoadingToggle] = useState(false);
  const [toggleError, setToggleError] = useState(null);

  useEffect(() => {
    loadPricingStatus();
  }, []);

  const loadPricingStatus = async () => {
    const response = await amplifyApi.getAdminPricing();
    if (response.success && response.configuration?.global) {
      const matrixVal = response.configuration.global.usePricingMatrix;
      setPricingMatrixEnabled(matrixVal === true || matrixVal === 'true');
    }
  };

  const handleToggleMatrix = async () => {
    setLoadingToggle(true);
    setToggleError(null);

    const response = await amplifyApi.togglePricingMatrix(!pricingMatrixEnabled);
    if (response.success) {
      setPricingMatrixEnabled(!pricingMatrixEnabled);
    } else {
      setToggleError(response.error || 'Failed to toggle pricing matrix');
    }

    setLoadingToggle(false);
  };

  // Build tabs list - conditionally include tabs based on brand features
  const tabs = [
    { id: 'pricing', label: 'Pricing', icon: DollarSign },
    { id: 'limits', label: 'Limits', icon: Gauge },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'products', label: 'Products', icon: Package },
    // Only show Commissions tab if referrals/commissions are enabled
    ...(isReferralsEnabled ? [{ id: 'commissions', label: 'Commissions', icon: Receipt }] : []),
    // Only show AI Stats tab if AI generation is enabled
    ...(isAiEnabled ? [{ id: 'ai-stats', label: 'AI Stats', icon: Sparkles }] : []),
    // Only show Monetization tab if monetization is enabled
    ...(isMonetizationEnabled ? [{ id: 'monetization', label: 'Monetization', icon: Megaphone }] : [])
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to={brand?.routes?.studio || '/studio-x7k9m2'}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-medium">Back to Studio</span>
            </Link>
            <div className="h-6 w-px bg-slate-200"></div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                <LayoutDashboard className="w-4 h-4 text-white" />
              </div>
              <h1 className="text-lg font-semibold text-slate-900">Admin Dashboard</h1>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-500">
              {user?.attributes?.email}
            </span>
            <button
              onClick={signOut}
              className="px-3 py-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Pricing Matrix Toggle - Hidden for fixed pricing mode brands */}
        {isFixedPricingMode ? (
          <div className="bg-amber-50 rounded-xl border border-amber-200 p-6 mb-8">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-lg">
                <Lock className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-amber-900">Fixed Pricing Mode</h2>
                <p className="text-sm text-amber-700 mt-1">
                  This brand uses fixed margin pricing. The pricing matrix toggle is disabled.
                  Margin can be adjusted in the Pricing tab below.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Pricing Matrix</h2>
                <p className="text-sm text-slate-500 mt-1">
                  {pricingMatrixEnabled
                    ? 'Dynamic pricing with tiers and category adjustments is active'
                    : 'Using flat 95% margin on all products (safe mode)'}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {toggleError && (
                  <div className="flex items-center gap-2 text-red-600 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    {toggleError}
                  </div>
                )}
                <button
                  onClick={handleToggleMatrix}
                  disabled={loadingToggle}
                  className={`relative inline-flex h-10 w-20 items-center rounded-full transition-colors ${
                    pricingMatrixEnabled ? 'bg-green-500' : 'bg-slate-300'
                  } ${loadingToggle ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  {loadingToggle ? (
                    <Loader2 className="w-5 h-5 animate-spin text-white absolute left-1/2 -translate-x-1/2" />
                  ) : pricingMatrixEnabled ? (
                    <ToggleRight className="w-8 h-8 text-white absolute right-1" />
                  ) : (
                    <ToggleLeft className="w-8 h-8 text-slate-500 absolute left-1" />
                  )}
                </button>
                <span className={`text-sm font-medium ${pricingMatrixEnabled ? 'text-green-600' : 'text-slate-500'}`}>
                  {pricingMatrixEnabled ? 'ON' : 'OFF'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 border-b border-slate-200 pb-4">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-blue-600 text-white'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-xl border border-slate-200">
          {activeTab === 'pricing' && <PricingTab />}
          {activeTab === 'limits' && <LimitsTab />}
          {activeTab === 'users' && <UsersTab />}
          {activeTab === 'products' && <ProductsTab />}
          {activeTab === 'commissions' && isReferralsEnabled && <CommissionsTab />}
          {activeTab === 'ai-stats' && isAiEnabled && <AiStatsTab />}
          {activeTab === 'monetization' && isMonetizationEnabled && <MonetizationTab />}
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
